import { Injectable, inject, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, retry } from 'rxjs';
import { Call, Device } from '@twilio/voice-sdk';
import { environment } from '../../../../environments/environment';
import { VoiceAccessTokenResponse } from '../models/voice-token.model';
import { CallApiService } from './call-api.service';

/**
 * Estado del Twilio Device.
 *
 * - `IDLE`: aún no se intentó registrar.
 * - `REGISTERING`: pidiendo token o llamando `device.register()`.
 * - `READY`: registrado, listo para llamar y recibir.
 * - `OFFLINE`: el device se destruyó (logout).
 * - `NO_CONFIG`: el backend respondió 409 al pedir token (Twilio no configurado).
 * - `ERROR`: error inesperado (red, credenciales inválidas, etc.).
 */
export type VoiceDeviceState = 'IDLE' | 'REGISTERING' | 'READY' | 'OFFLINE' | 'NO_CONFIG' | 'ERROR';

/**
 * Estado de la llamada actual del Device. Soporta una sola llamada simultánea.
 *
 * - `null`: sin llamada activa.
 * - `INCOMING`: recibiendo, esperando accept/reject.
 * - `CONNECTING`: outbound iniciada, esperando que el destinatario conteste.
 * - `IN_CALL`: hablando.
 */
export type CallPhase = 'INCOMING' | 'CONNECTING' | 'IN_CALL';

export interface ActiveCall {
  call: Call;
  phase: CallPhase;
  from: string;
  to: string;
  callSid?: string;
  /** Id numérico del CRM (tabla call_sessions). Necesario para los endpoints /api/calls/routing/{callId}/*. */
  crmCallId?: number;
  startedAt?: number; // ms epoch — solo cuando entra a IN_CALL
  muted: boolean;
  isOnHold: boolean;
}

const REFRESH_BUFFER_MS = 5 * 60 * 1000; // refrescar 5 min antes del expiresAt

@Injectable({ providedIn: 'root' })
export class VoiceDeviceService {
  private readonly http = inject(HttpClient);
  private readonly zone = inject(NgZone);
  private readonly callApi = inject(CallApiService);
  private readonly base = `${environment.apiUrl}api/calls/device`;

  private device: Device | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private currentIdentity: string | null = null;

  private readonly _deviceState = new BehaviorSubject<VoiceDeviceState>('IDLE');
  readonly deviceState$ = this._deviceState.asObservable();

  private readonly _activeCall = new BehaviorSubject<ActiveCall | null>(null);
  readonly activeCall$ = this._activeCall.asObservable();

  private readonly _lastError = new BehaviorSubject<string | null>(null);
  readonly lastError$ = this._lastError.asObservable();

  get state(): VoiceDeviceState { return this._deviceState.value; }
  get activeCall(): ActiveCall | null { return this._activeCall.value; }
  get identity(): string | null { return this.currentIdentity; }

  requestAccessToken(): Observable<VoiceAccessTokenResponse> {
    return this.http.post<VoiceAccessTokenResponse>(`${this.base}/token`, {});
  }

  /**
   * Inicializa el Device si todavía no está activo. Idempotente — si ya hay un
   * Device READY o REGISTERING, no hace nada.
   */
  init(): void {
    if (this.device || this.state === 'REGISTERING') return;
    this.setState('REGISTERING');
    this._lastError.next(null);

    this.requestAccessToken().subscribe({
      next: (res) => this.bootstrapDevice(res),
      error: (err) => this.handleTokenError(err)
    });
  }

  private bootstrapDevice(res: VoiceAccessTokenResponse): void {
    try {
      const device = new Device(res.token, {
        logLevel: 1,
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU]
      });

      this.currentIdentity = res.identity;

      device.on('registered', () => this.zone.run(() => this.setState('READY')));
      device.on('unregistered', () => this.zone.run(() => this.setState('OFFLINE')));
      device.on('destroyed', () => this.zone.run(() => this.setState('OFFLINE')));
      device.on('error', (err: { message?: string }) => this.zone.run(() => {
        this._lastError.next(err?.message || 'Error del Device');
        this.setState('ERROR');
      }));
      device.on('tokenWillExpire', () => this.refreshToken());
      device.on('incoming', (call: Call) => this.zone.run(() => this.handleIncoming(call)));

      this.device = device;
      this.scheduleRefresh(res.expiresAt);
      device.register().catch((err) => {
        this._lastError.next(err?.message || 'No se pudo registrar el Device');
        this.setState('ERROR');
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error inicializando el Device';
      this._lastError.next(msg);
      this.setState('ERROR');
    }
  }

  private handleTokenError(err: { status?: number; error?: { message?: string } }): void {
    if (err?.status === 409) {
      this._lastError.next(err.error?.message || 'Twilio no está configurado para este business.');
      this.setState('NO_CONFIG');
      return;
    }
    this._lastError.next(err?.error?.message || 'Error al obtener el AccessToken');
    this.setState('ERROR');
  }

  private refreshToken(): void {
    this.requestAccessToken().subscribe({
      next: (res) => {
        try {
          this.device?.updateToken(res.token);
          this.scheduleRefresh(res.expiresAt);
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Error refrescando el token';
          this._lastError.next(msg);
          this.setState('ERROR');
        }
      },
      error: (err) => this.handleTokenError(err)
    });
  }

  private scheduleRefresh(expiresAt: string): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    const ms = new Date(expiresAt).getTime() - Date.now() - REFRESH_BUFFER_MS;
    if (ms <= 0) {
      // Si por alguna razón el expiresAt ya está cerca, refrescar enseguida.
      this.refreshToken();
      return;
    }
    this.refreshTimer = setTimeout(() => this.refreshToken(), ms);
  }

  /**
   * Inicia una llamada saliente. `to` debe estar en formato E.164.
   *
   * Éste es el ÚNICO camino por el que sale una llamada desde el marcador: el
   * navegador la origina y el webhook `/webhooks/twilio/twiml` la registra en el
   * CRM y marca al destinatario con un número real de la empresa. Llamar además
   * a `POST /api/calls`, como se hacía antes, creaba una SEGUNDA llamada al
   * mismo destinatario.
   */
  async connect(to: string): Promise<void> {
    if (!this.device || this.state !== 'READY') {
      throw new Error('El Device no está listo todavía.');
    }
    if (this._activeCall.value) {
      throw new Error('Ya hay una llamada activa.');
    }

    try {
      const call = await this.device.connect({
        params: { To: to }
      });
      this.wireCall(call, {
        phase: 'CONNECTING',
        from: this.currentIdentity || '',
        to,
        muted: false,
        isOnHold: false
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'No se pudo iniciar la llamada';
      this._lastError.next(msg);
      throw err;
    }
  }

  /**
   * Asocia un `crmCallId` (id numérico del backend) a la llamada activa actual.
   * Lo resuelve `resolveCrmCallId()` vía `GET /api/calls/sid/{callSid}`, tanto en
   * entrantes como en salientes.
   */
  setCrmCallId(id: number): void {
    const cur = this._activeCall.value;
    if (!cur) return;
    this._activeCall.next({ ...cur, crmCallId: id });
  }

  /** Marca/desmarca el estado isOnHold tras una respuesta exitosa del backend. */
  setOnHold(isOnHold: boolean): void {
    const cur = this._activeCall.value;
    if (!cur) return;
    this._activeCall.next({ ...cur, isOnHold });
  }

  private handleIncoming(call: Call): void {
    if (this._activeCall.value) {
      // Hay otra llamada activa — rechazar automáticamente.
      try { call.reject(); } catch { /* ignore */ }
      return;
    }
    const from = (call.parameters && (call.parameters as Record<string, string>)['From']) || '';
    const to = (call.parameters && (call.parameters as Record<string, string>)['To']) || '';
    this.wireCall(call, { phase: 'INCOMING', from, to, muted: false, isOnHold: false });
    this.resolveCrmCallId(call);
  }

  /**
   * Resuelve el id de `call_session` a partir del Call SID de Twilio — necesario
   * para transferir y para el panel de contexto CRM.
   *
   * Se reintenta porque en las salientes hay una carrera real: la sesión la crea
   * el webhook del TwiML, que puede llegar milisegundos después de que el SDK
   * dispare `accept` en el navegador.
   */
  private resolveCrmCallId(call: Call): void {
    const sid = (call.parameters && (call.parameters as Record<string, string>)['CallSid']) || '';
    if (!sid) return;
    this.callApi
      .getCallBySid(sid)
      .pipe(retry({ count: 3, delay: 1000 }))
      .subscribe({
        next: (res) => {
          const cur = this._activeCall.value;
          if (cur && cur.call === call) this.setCrmCallId(res.id);
        },
        error: () => { /* sin log: el widget deja transferir deshabilitado */ }
      });
  }

  private wireCall(call: Call, init: Omit<ActiveCall, 'call' | 'callSid' | 'crmCallId' | 'startedAt'>): void {
    const callSid = (call.parameters && (call.parameters as Record<string, string>)['CallSid']) || undefined;
    const active: ActiveCall = { call, callSid, ...init };
    this._activeCall.next(active);

    call.on('accept', () => this.zone.run(() => {
      const cur = this._activeCall.value;
      if (!cur || cur.call !== call) return;
      // El CallSid de una saliente no existe hasta que la llamada se acepta.
      const sid = (call.parameters && (call.parameters as Record<string, string>)['CallSid']) || undefined;
      this._activeCall.next({ ...cur, phase: 'IN_CALL', startedAt: Date.now(), callSid: cur.callSid ?? sid });
      if (!cur.crmCallId) this.resolveCrmCallId(call);
    }));

    call.on('disconnect', () => this.zone.run(() => {
      const cur = this._activeCall.value;
      if (cur && cur.call === call) this._activeCall.next(null);
    }));

    call.on('cancel', () => this.zone.run(() => {
      const cur = this._activeCall.value;
      if (cur && cur.call === call) this._activeCall.next(null);
    }));

    call.on('reject', () => this.zone.run(() => {
      const cur = this._activeCall.value;
      if (cur && cur.call === call) this._activeCall.next(null);
    }));

    call.on('error', (err: { message?: string }) => this.zone.run(() => {
      this._lastError.next(err?.message || 'Error de la llamada');
    }));

    call.on('mute', (isMuted: boolean) => this.zone.run(() => {
      const cur = this._activeCall.value;
      if (cur && cur.call === call) this._activeCall.next({ ...cur, muted: isMuted });
    }));
  }

  accept(): void {
    const cur = this._activeCall.value;
    if (!cur || cur.phase !== 'INCOMING') return;
    cur.call.accept();
  }

  reject(): void {
    const cur = this._activeCall.value;
    if (!cur) return;
    try { cur.call.reject(); } catch { /* ignore */ }
    this._activeCall.next(null);
  }

  hangup(): void {
    const cur = this._activeCall.value;
    if (!cur) return;
    try { cur.call.disconnect(); } catch { /* ignore */ }
  }

  setMuted(muted: boolean): void {
    const cur = this._activeCall.value;
    if (!cur) return;
    try { cur.call.mute(muted); } catch { /* ignore */ }
  }

  toggleMute(): void {
    const cur = this._activeCall.value;
    if (!cur) return;
    this.setMuted(!cur.muted);
  }

  shutdown(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.device) {
      try { this.device.destroy(); } catch { /* ignore */ }
      this.device = null;
    }
    this._activeCall.next(null);
    this.currentIdentity = null;
    this.setState('OFFLINE');
  }

  private setState(s: VoiceDeviceState): void {
    if (this._deviceState.value !== s) this._deviceState.next(s);
  }
}
